import { el } from '../dom.js';
import type { I18n } from '../i18n.js';
import { api } from '../session.js';

/**
 * Post-debrief demographics survey (§23.3 WEIRD-correction). Every question is
 * opt-in; a visible "Skip" and per-question "prefer not to say" keep pressure low.
 *
 * Runs AFTER the debrief so axis-decomposition framing cannot prime the answers.
 */
export interface SurveyProps {
  i18n: I18n;
  onDone: () => void;
}

const OPTIONS = {
  region: ['NA', 'LATAM', 'EU', 'MENA', 'SSA', 'SA', 'EA', 'SEA', 'OC', 'prefer_not_to_say'],
  ageBand: ['18-24', '25-34', '35-44', '45-54', '55-64', '65+', 'prefer_not_to_say'],
  gender: ['woman', 'man', 'non_binary', 'other', 'prefer_not_to_say'],
  education: ['primary', 'secondary', 'vocational', 'bachelor', 'graduate', 'prefer_not_to_say'],
} as const;

export function SurveyView({ i18n, onDone }: SurveyProps): HTMLElement {
  const answers: Record<string, string> = {};
  const status = el('p', { class: 'hero-lede', role: 'status', 'aria-live': 'polite' }, '');

  function question(key: keyof typeof OPTIONS): HTMLElement {
    const select = el('select', { name: key, id: `survey-${key}` }) as HTMLSelectElement;
    select.appendChild(el('option', { value: '' }, i18n.t('survey.choose')));
    for (const opt of OPTIONS[key]) {
      select.appendChild(el('option', { value: opt }, i18n.t(`survey.${key}.${opt}`)));
    }
    select.addEventListener('change', () => { answers[key] = select.value; });
    return el('label', { class: 'survey-q', for: `survey-${key}` }, [
      el('span', {}, i18n.t(`survey.${key}.label`)),
      select,
    ]);
  }

  const form = el('div', { class: 'survey' }, [
    question('region'),
    question('ageBand'),
    question('gender'),
    question('education'),
  ]);

  const submitBtn = el('button', {}, i18n.t('survey.submit')) as HTMLButtonElement;
  submitBtn.addEventListener('click', async () => {
    for (const k of ['region', 'ageBand', 'gender', 'education']) {
      if (!answers[k]) {
        status.textContent = i18n.t('survey.incomplete');
        return;
      }
    }
    submitBtn.disabled = true;
    try {
      await api.submitDemographics({
        region: answers.region!,
        ageBand: answers.ageBand!,
        gender: answers.gender!,
        education: answers.education!,
      });
      status.textContent = i18n.t('survey.thanks');
      setTimeout(onDone, 900);
    } catch {
      submitBtn.disabled = false;
      status.textContent = i18n.t('errors.network');
    }
  });

  const skipBtn = el('button', { class: 'secondary' }, i18n.t('survey.skip'));
  skipBtn.addEventListener('click', () => onDone());

  return el('section', { 'aria-label': 'Survey', class: 'stack-lg' }, [
    el('header', {}, [
      el('h1', {}, i18n.t('survey.heading')),
      el('p', { class: 'hero-lede' }, i18n.t('survey.intro')),
    ]),
    form,
    el('div', { class: 'actions' }, [
      el('div', { class: 'actions-primary' }, [submitBtn]),
      el('div', { class: 'actions-secondary' }, [skipBtn]),
    ]),
    status,
  ]);
}
